import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';

export interface StatusMultiSelectProps<T extends string> {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  selectedStatuses: T[];
  onStatusesChange: (statuses: T[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allLabel?: string;
  pluralLabel?: string;
  summary?: string;
  visuallyHiddenLabel?: boolean;
}

export function StatusMultiSelect<T extends string>({
  label,
  options,
  selectedStatuses,
  onStatusesChange,
  open,
  onOpenChange,
  allLabel = 'All statuses',
  pluralLabel = 'statuses',
  summary: summaryOverride,
  visuallyHiddenLabel = false,
}: StatusMultiSelectProps<T>) {
  const id = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 0, top: 0, maxHeight: 384 });
  const summary =
    summaryOverride ??
    (selectedStatuses.length === 0
      ? allLabel
      : selectedStatuses.length === 1
        ? (options.find((option) => option.value === selectedStatuses[0])?.label ?? label)
        : `${selectedStatuses.length} ${pluralLabel}`);

  useLayoutEffect(() => {
    if (!open) return;
    function positionMenu() {
      const button = buttonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const menu = menuRef.current;
      const options = optionsRef.current;
      // Include the options' full height even when their scroll area is constrained.
      const height = Math.min(
        384,
        menu && options ? menu.offsetHeight - options.clientHeight + options.scrollHeight : 384
      );
      const placeAbove = below < height && above > below;
      const maxHeight = Math.max(0, Math.min(384, placeAbove ? above : below));
      setPosition({
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 232)),
        top: placeAbove ? Math.max(8, rect.top - Math.min(height, maxHeight) - 4) : rect.bottom + 4,
        maxHeight,
      });
    }
    positionMenu();
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);
    return () => {
      window.removeEventListener('resize', positionMenu);
      window.removeEventListener('scroll', positionMenu, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleOutside(event: Event) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      onOpenChange(false);
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      onOpenChange(false);
      buttonRef.current?.focus();
    }
    document.addEventListener('pointerdown', handleOutside);
    document.addEventListener('focusin', handleOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handleOutside);
      document.removeEventListener('focusin', handleOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onOpenChange]);

  return (
    <div className="relative flex min-w-[180px] flex-col gap-1 text-small font-medium text-neutral-700">
      <label htmlFor={id} className={visuallyHiddenLabel ? 'sr-only' : undefined}>
        {label}
      </label>
      <button
        id={id}
        ref={buttonRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? `${id}-options` : undefined}
        aria-label={summary}
        className="focus-ring flex h-10 items-center justify-between gap-2 rounded-lg border border-neutral-200 bg-white px-3 text-left text-body font-normal text-neutral-900"
        onClick={() => onOpenChange(!open)}
        onKeyDown={(event) => {
          if (open && (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey))) {
            event.preventDefault();
            menuRef.current?.querySelector('input')?.focus();
          }
        }}
      >
        <span>{summary}</span>
        <ChevronDown className="h-4 w-4 text-neutral-400" aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <div
            id={`${id}-options`}
            ref={menuRef}
            role="dialog"
            aria-label={label}
            onKeyDown={(event) => {
              if (event.key !== 'Tab') return;
              const inputs = menuRef.current?.querySelectorAll('input, button');
              if (!inputs?.length) return;
              const boundary = event.shiftKey ? inputs[0] : inputs[inputs.length - 1];
              if (event.target !== boundary) return;
              onOpenChange(false);
              buttonRef.current?.focus();
              if (event.shiftKey) event.preventDefault();
            }}
            // The portal needs viewport coordinates to escape clipped, scrollable tiles.
            style={position}
            className="fixed z-50 flex w-56 max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white p-2 shadow-lg"
          >
            <div ref={optionsRef} className="min-h-0 overflow-y-auto">
              {options.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-body font-normal text-neutral-800 hover:bg-backoffice-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedStatuses.includes(option.value)}
                    onChange={() =>
                      onStatusesChange(
                        selectedStatuses.includes(option.value)
                          ? selectedStatuses.filter((status) => status !== option.value)
                          : [...selectedStatuses, option.value]
                      )
                    }
                    className="h-4 w-4 rounded border-neutral-300 text-backoffice-600"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            <div className="mt-1 flex shrink-0 items-center justify-between gap-2 border-t border-neutral-200 pt-2">
              <button
                type="button"
                className="focus-ring whitespace-nowrap rounded-md px-1 py-1 text-small font-normal text-neutral-700 hover:bg-backoffice-50"
                onClick={() => onStatusesChange(options.map((option) => option.value))}
              >
                Select all
              </button>
              <button
                type="button"
                className="focus-ring inline-flex items-center gap-1 whitespace-nowrap rounded-md px-1 py-1 text-small font-normal text-neutral-700 hover:bg-backoffice-50"
                onClick={() => onStatusesChange([])}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
                Clear selection
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
